import { useCallback, useMemo } from "react";
import { ScrollView } from "react-native";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { ManageExceptionsContent } from "@src/features/alarms/components/ManageExceptionsContent";
import { useAggregatedExceptions } from "@src/features/alarms/hooks/useAggregatedExceptions";
import { useSiteCameraKeys } from "@src/features/cameras/hooks/useSiteCameraKeys";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { buildAlarmSettingsRoutes } from "@src/features/alarms/utils/alarm-settings-routes";
import { useExceptionSuggestions } from "@src/services/api/hooks";
import { makeDateTimeRecord } from "@visioni/schedules";

export default function ManageExceptionsScreen() {
  const { theme } = useUnistyles();
  const { bottom } = useSafeAreaInsets();
  const router = useRouter();
  const { tenantId, siteId } = useLocalSearchParams<{ tenantId: string; siteId: string }>();
  const { cameraKeys } = useSiteCameraKeys(siteId);
  const { headerLeft } = useHeaderBackButton();
  const routes = useMemo(() => buildAlarmSettingsRoutes(tenantId, siteId), [tenantId, siteId]);
  const { isLoading, exceptions: aggregatedExceptions } = useAggregatedExceptions(siteId, cameraKeys);

  const firstCameraKey = cameraKeys[0];
  const suggestionsQuery = useExceptionSuggestions(siteId, firstCameraKey);
  const suggestions = suggestionsQuery.data ?? [];
  const suggestionsLoaded = !suggestionsQuery.isLoading;

  const handleCreateException = useCallback(() => {
    if (!suggestionsLoaded || suggestions.length > 0) {
      router.push(routes.createException as never);
      return;
    }

    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const day = now.getDate();

    router.push({
      pathname: routes.editExceptionDetails as never,
      params: {
        exception: JSON.stringify({
          reason: "",
          action: "armed",
          period: {
            start: makeDateTimeRecord(year, month, day, 0, 0),
            end: makeDateTimeRecord(year, month, day, 24, 0),
          },
        }),
        isNew: "true",
        allCameraKeys: JSON.stringify(cameraKeys),
      },
    });
  }, [suggestionsLoaded, suggestions.length, router, routes, cameraKeys]);

  return (
    <>
      <Stack.Screen options={{ title: "Exceptions", headerLeft }} />
      <ScrollView
        style={{ flex: 1, backgroundColor: theme.colors.background }}
        contentContainerStyle={{
          paddingHorizontal: theme.spacing["3xl"],
          paddingTop: theme.spacing["4xl"],
          paddingBottom: bottom + theme.spacing["6xl"],
          flexGrow: 1,
          backgroundColor: theme.colors.background,
        }}
        showsVerticalScrollIndicator={false}
      >
        <ManageExceptionsContent
          isLoading={isLoading}
          exceptions={aggregatedExceptions}
          onEditException={(group) =>
            router.push({
              pathname: routes.editException as never,
              params: {
                exception: JSON.stringify({ reason: group.reason, action: group.action, period: group.period }),
                includedCameraKeys: JSON.stringify(group.cameraKeys),
                timezone: group.timezone,
              },
            })
          }
          onCreateException={handleCreateException}
        />
      </ScrollView>
    </>
  );
}
